import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from '../../hooks/useTranslation';

// Add NavLink component definition
const NavLink = ({ to, icon, children, className = "" }) => {
  const location = useLocation();
  
  const isActive = () => {
    if (to === '/') {
      return location?.pathname === '/';
    }
    return location?.pathname?.startsWith(to);
  };

  return (
    <Link
      to={to}
      className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-warm ${
        isActive()
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
      } ${className}`}
    >
      <Icon name={icon} size={16} />
      <span>{children}</span>
    </Link>
  );
};

const Header = ({ currentUser, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const navigationItems = [
    { label: t('nav.dashboard'), path: '/', icon: 'Home' },
    { label: t('nav.guests'), path: '/guest-management', icon: 'Users' },
    { label: t('nav.dishes'), path: '/dish-library', icon: 'ChefHat' },
    { label: t('nav.planEvent'), path: '/dinner-planning', icon: 'Calendar' },
  ];

  const isActivePath = (path) => {
    if (path === '/') {
      return location?.pathname === '/';
    }
    return location?.pathname?.startsWith(path);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleUserMenuToggle = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    if (onLogout) {
      onLogout();
    }
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-navigation bg-card border-b border-border">
        <div className="flex items-center justify-between h-16 px-4 lg:px-6">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 text-primary hover:text-primary/80 transition-warm">
            <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
              <Icon name="Utensils" size={20} color="white" />
            </div>
            <span className="text-xl font-semibold text-foreground">Dinner Memory</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            <NavLink to="/dinner-planning" icon="Calendar">
              {t('nav.dinnerPlanning')}
            </NavLink>
            <NavLink to="/dish-library" icon="ChefHat">
              {t('nav.dishLibrary')}
            </NavLink>
            <NavLink to="/guest-management" icon="Users">
              {t('nav.guestManagement')}
            </NavLink>
            <NavLink to="/ai-assistant" icon="Bot">
              {t('nav.aiAssistant')}
            </NavLink>
          </nav>

          {/* Desktop User Menu */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Language Switcher */}
            <LanguageSwitcher />
            
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleUserMenuToggle}
                className="flex items-center space-x-2"
              >
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-foreground">
                    {currentUser?.name?.charAt(0) || 'U'}
                  </span>
                </div>
                <Icon name="ChevronDown" size={16} />
              </Button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-lg shadow-warm-lg z-50">
                  <div className="p-2">
                    <div className="px-3 py-2 text-sm text-muted-foreground border-b border-border">
                      {currentUser?.email || 'user@example.com'}
                    </div>
                    <Link
                      to="/account"
                      className="flex items-center space-x-2 px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-warm"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Icon name="Settings" size={16} />
                      <span>{t('account.settings')}</span>
                    </Link>
                    <Link
                      to="/help"
                      className="flex items-center space-x-2 px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-warm"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Icon name="HelpCircle" size={16} />
                      <span>{t('account.help')}</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-error hover:bg-muted rounded-md transition-warm"
                    >
                      <Icon name="LogOut" size={16} />
                      <span>{t('account.signOut')}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMobileMenuToggle}
            className="lg:hidden"
          >
            <Icon name={mobileMenuOpen ? "X" : "Menu"} size={20} />
          </Button>
        </div>
      </header>
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-mobile-menu lg:hidden">
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={handleMobileMenuToggle} />
          <div className="fixed top-0 right-0 h-full w-80 bg-card border-l border-border shadow-warm-lg">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <span className="text-lg font-semibold text-foreground">Menu</span>
              <Button variant="ghost" size="sm" onClick={handleMobileMenuToggle}>
                <Icon name="X" size={20} />
              </Button>
            </div>
            
            <nav className="p-4 space-y-2">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={handleMobileMenuToggle}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-warm ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.label}</span>
                </Link>
              ))}
            </nav>

            {/* Language Switcher in Mobile Menu */}
            <div className="px-4 py-2 border-t border-border">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">{t('account.language')}</span>
                <LanguageSwitcher showLabels={true} />
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
              <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-base font-medium text-primary-foreground">
                    {currentUser?.name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {currentUser?.name || 'User'}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {currentUser?.email || 'user@example.com'}
                  </p>
                </div>
              </div>
              
              <div className="mt-3 space-y-1">
                <Link
                  to="/account"
                  onClick={handleMobileMenuToggle}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-warm"
                >
                  <Icon name="Settings" size={16} />
                  <span>{t('account.settings')}</span>
                </Link>
                <Link
                  to="/help"
                  onClick={handleMobileMenuToggle}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-warm"
                >
                  <Icon name="HelpCircle" size={16} />
                  <span>{t('account.help')}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-error hover:bg-muted rounded-md transition-warm"
                >
                  <Icon name="LogOut" size={16} />
                  <span>{t('account.signOut')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Click outside handler for user menu */}
      {userMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setUserMenuOpen(false)}
        />
      )}
    </>
  );
};

export default Header;